import { computed, ref, shallowRef, toValue, type MaybeRefOrGetter, type ShallowRef } from 'vue';

type SortDirection = 'asc' | 'desc';

type InternalUserRow = {
    tenantName: string;
    name: string;
    email: string;
};

const compareValues = (a: unknown, b: unknown) =>
    String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });

export const useInternalUsersTable = <T extends InternalUserRow>(rows: MaybeRefOrGetter<T[]>) => {
    const search = ref('');
    const tenantFilter = ref('');
    const sortBy: ShallowRef<keyof T> = shallowRef('tenantName');
    const sortDirection = ref<SortDirection>('asc');

    const tenantNames = computed(() =>
        [...new Set(toValue(rows).map((row) => row.tenantName))].sort(compareValues)
    );

    const setSort = (field: keyof T, direction: SortDirection) => {
        sortBy.value = field;
        sortDirection.value = direction;
    };

    const activeSort = (field: keyof T) =>
        sortBy.value === field ? sortDirection.value : undefined;

    const visibleRows = computed(() => {
        const term = search.value.trim().toLowerCase();
        const factor = sortDirection.value === 'asc' ? 1 : -1;

        return toValue(rows)
            .filter(
                (row) =>
                    (!tenantFilter.value || row.tenantName === tenantFilter.value) &&
                    (!term ||
                        row.name.toLowerCase().includes(term) ||
                        row.email.toLowerCase().includes(term))
            )
            .sort(
                (a, b) =>
                    factor * compareValues(a[sortBy.value], b[sortBy.value]) ||
                    compareValues(a.name, b.name)
            );
    });

    return { search, tenantFilter, tenantNames, setSort, activeSort, visibleRows };
};
